import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import AppTopBar from '../components/layout/AppTopBar';
import Btn from '../components/ui/Btn';
import { Avatar, Card, Input, Spinner, Badge } from '../components/ui/primitives';
import { Icon } from '../components/ui/Icon';
import { ErrorBanner } from './auth/SignInScreen';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../lib/toast';
import { api } from '../lib/api';
import { getUserPlan, getOwnerUsage } from '../lib/plan';
import { useIsMobile } from '../lib/useBreakpoint';

const TABS = [
  { k: 'profile',       label: 'Profile' },
  { k: 'billing',       label: 'Billing & plan' },
  { k: 'security',      label: 'Security' },
  { k: 'notifications', label: 'Notifications' },
  { k: 'api',           label: 'API keys' },
  { k: 'sessions',      label: 'Sessions' },
];

export default function AccountScreen() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const showUpgraded = searchParams.get('upgraded') === 'true';

  // Initial tab can be set via ?tab=billing (deep-link from UsageBar "Upgrade" clicks)
  const initialTab = searchParams.get('tab') || 'profile';
  const [tab, setTab] = useState(
    TABS.find(t => t.k === initialTab) ? initialTab : 'profile'
  );

  const handleTabChange = (newTab) => {
    setTab(newTab);
    searchParams.set('tab', newTab);
    setSearchParams(searchParams, { replace: true });
  };

  // Clear the ?upgraded=true from URL after showing banner so reload doesn't re-show it
  useEffect(() => {
    if (showUpgraded) {
      const timer = setTimeout(() => {
        searchParams.delete('upgraded');
        setSearchParams(searchParams, { replace: true });
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showUpgraded, searchParams, setSearchParams]);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <AppTopBar
        breadcrumb={
          <>
            <span style={{ color: 'var(--text-3)' }}>Settings</span>
            <Icon.chevR size={12} stroke="var(--text-muted)" />
            <span style={{ color: 'var(--text)' }}>Account</span>
          </>
        }
      />

      <div style={{
        maxWidth: 960, margin: '0 auto',
        padding: isMobile ? '20px 16px 48px' : '32px 32px 60px',
      }}>
        <div style={{ marginBottom: 24, paddingTop: 4 }}>
          <h1 style={{
            fontSize: 26, fontWeight: 700, letterSpacing: '-0.025em',
            marginBottom: 4, color: 'var(--text)',
          }}>
            Account
          </h1>
          <p style={{ fontSize: 13.5, color: 'var(--text-3)' }}>
            Your personal settings across all workspaces.
          </p>
        </div>

        {showUpgraded && (
          <div style={{
            marginBottom: 20, padding: '14px 18px',
            background: 'var(--ok-bg)',
            border: '1px solid rgba(61,199,91,0.3)',
            borderRadius: 'var(--r-md)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'var(--ok)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon.check size={16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ok)', marginBottom: 2 }}>
                Upgrade successful
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                Your plan has been updated. It may take a few seconds to reflect in your workspaces.
              </div>
            </div>
          </div>
        )}

        {/* Tab strip — horizontally scrollable on mobile */}
        <div style={{
          display: 'flex', gap: 4,
          borderBottom: '1px solid var(--border)',
          marginBottom: 24, overflowX: 'auto',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}>
          {TABS.map(t => (
            <button
              key={t.k}
              type="button"
              onClick={() => handleTabChange(t.k)}
              style={{
                padding: isMobile ? '12px 14px' : '10px 14px',
                border: 'none',
                background: 'transparent',
                fontSize: 13, fontWeight: 600,
                color: tab === t.k ? 'var(--text)' : 'var(--text-3)',
                borderBottom: tab === t.k ? '2px solid var(--accent)' : '2px solid transparent',
                cursor: 'pointer', fontFamily: 'inherit',
                marginBottom: -1, whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'color 0.12s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'profile'       && <ProfileTab navigate={navigate} />}
        {tab === 'billing'       && <BillingTab />}
        {tab === 'security'      && <SecurityTab />}
        {tab === 'notifications' && <NotificationsTab />}
        {tab === 'api'           && <ComingSoonTab title="API keys" desc="Issue scoped API tokens to integrate MagicIntake with your other tools." />}
        {tab === 'sessions'      && <ComingSoonTab title="Active sessions" desc="View and revoke devices currently signed in to your account." />}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Profile tab — name, email (read-only), team-member rows for context
// ────────────────────────────────────────────────────────────────────────────
function ProfileTab({ navigate }) {
  const isMobile = useIsMobile();
  const { user, signOut } = useAuth();
  const meta = user?.user_metadata || {};

  const [firstName, setFirstName] = useState(meta.first_name || '');
  const [lastName, setLastName]   = useState(meta.last_name || '');

  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(null);

  const [memberships, setMemberships] = useState([]);
  const [memLoading, setMemLoading]   = useState(true);

  // Load all team memberships for context
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('team_members')
          .select(`
            id, is_owner, language,
            teams ( id, name, plan, primary_color ),
            team_roles ( id, name )
          `)
          .eq('user_id', user.id);
        if (!cancelled) setMemberships(data || []);
      } catch (err) {
        console.warn('Memberships load failed:', err);
      } finally {
        if (!cancelled) setMemLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const dirty =
    firstName !== (meta.first_name || '') ||
    lastName  !== (meta.last_name  || '');

  const save = async () => {
    setError(null);
    setSuccess(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    setSaving(true);
    try {
      // 1. Update auth user metadata (canonical name)
      const { error: updErr } = await supabase.auth.updateUser({
        data: {
          first_name: firstName.trim(),
          last_name:  lastName.trim(),
        },
      });
      if (updErr) throw updErr;

      // 2. Sync the name to all team_members rows (best effort)
      const { error: memErr } = await supabase
        .from('team_members')
        .update({
          first_name: firstName.trim(),
          last_name:  lastName.trim(),
        })
        .eq('user_id', user.id);
      if (memErr) console.warn('Sync to team_members failed:', memErr);

      setSuccess('Profile saved.');
    } catch (err) {
      console.error('Save profile failed:', err);
      setError(err.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  // Delete account — softer than the prototype implies; we sign out and direct to support
  const requestDelete = async () => {
    const r = await confirm({
      title: 'Delete your MagicIntake account?',
      message: 'Account deletion requires manual review. We\'ll open a support email so we can confirm and process the request.',
      confirmLabel: 'Open support email',
      confirmVariant: 'danger',
    });
    if (!r.confirmed) return;
    const subject = encodeURIComponent('Account deletion request');
    const body = encodeURIComponent(
      `Hi MagicIntake team,\n\nPlease delete my account.\n\nEmail: ${user.email}\nUser ID: ${user.id}\n\nThanks.`
    );
    window.location.href = `mailto:support@magicintake.com?subject=${subject}&body=${body}`;
  };

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || user?.email || '?';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : 'repeat(auto-fit, minmax(380px, 1fr))',
      gap: isMobile ? 12 : 16,
    }}>
      {/* Personal info */}
      <Card style={{ padding: isMobile ? 16 : 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Personal info</div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          marginBottom: 20, paddingBottom: 20,
          borderBottom: '1px solid var(--border)',
        }}>
          <Avatar name={fullName} color="#3dc75b" size={68} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{fullName}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
              Photo upload coming soon
            </div>
          </div>
        </div>

        {error   && <ErrorBanner message={error} style={{ marginBottom: 12 }} />}
        {success && (
          <div style={{
            padding: '10px 12px', marginBottom: 12,
            background: 'var(--ok-bg)', border: '1px solid rgba(61,199,91,0.22)',
            color: 'var(--ok)', borderRadius: 10, fontSize: 12.5,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Icon.check size={14} />
            {success}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 10, marginBottom: 12,
        }}>
          <FormField
            label="First name"
            value={firstName}
            onChange={setFirstName}
            disabled={saving}
          />
          <FormField
            label="Last name"
            value={lastName}
            onChange={setLastName}
            disabled={saving}
          />
        </div>

        <FormField
          label="Email"
          value={user?.email || ''}
          icon={Icon.mail}
          readOnly
          hint="Email changes require contacting support."
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Btn
            variant="primary"
            size="sm"
            icon={saving ? null : Icon.check}
            onClick={save}
            disabled={!dirty || saving}
          >
            {saving ? <><Spinner size={12} color="rgba(255,255,255,0.6)" /> Saving…</> : 'Save changes'}
          </Btn>
        </div>
      </Card>

      {/* Right column: workspaces + delete */}
      <div>
        <Card style={{ padding: isMobile ? 16 : 20, marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Your workspaces</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
            {memLoading ? 'Loading…' : `${memberships.length} workspace${memberships.length === 1 ? '' : 's'}`}
          </div>

          {memLoading ? (
            <div style={{
              padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Spinner size={14} color="var(--text-muted)" />
            </div>
          ) : memberships.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '8px 0' }}>
              You're not a member of any workspaces yet.
            </div>
          ) : (
            memberships.map((m, i) => {
              if (!m.teams) return null;
              const name = m.teams.name;
              const initials = name.split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
              return (
                <div
                  key={m.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 0',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: 7,
                    background: m.teams.primary_color || '#0a0f2e',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                  }}>
                    {initials}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                      {m.is_owner ? 'Owner' : (m.team_roles?.name || 'Member')}
                      {' · '}
                      {(m.teams.plan || 'free').charAt(0).toUpperCase() + (m.teams.plan || 'free').slice(1)}
                    </div>
                  </div>
                  <Btn
                    variant="ghost"
                    size="xs"
                    iconRight={Icon.arrowR}
                    onClick={() => navigate(`/workspace/${m.teams.id}`)}
                  >
                    Open
                  </Btn>
                </div>
              );
            })
          )}
        </Card>

        <Card style={{ padding: 20, borderColor: 'rgba(239,68,68,0.22)' }}>
          <div style={{
            fontSize: 14, fontWeight: 700, marginBottom: 6, color: 'var(--danger)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Icon.alert size={14} />
            Delete account
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14 }}>
            Permanently remove your account and all associated data. Owned workspaces must be deleted or transferred first.
          </div>
          <Btn
            variant="outline"
            size="sm"
            onClick={requestDelete}
            style={{ color: 'var(--danger)', borderColor: 'rgba(239,68,68,0.35)' }}
          >
            Request account deletion
          </Btn>
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Security tab — password change
// ────────────────────────────────────────────────────────────────────────────
function SecurityTab() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(null);

  const submit = async () => {
    setError(null);
    setSuccess(null);

    if (newPw.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setError('Passwords do not match.');
      return;
    }
    if (!currentPw) {
      setError('Please enter your current password to confirm it\'s you.');
      return;
    }

    setSaving(true);
    try {
      // Verify current password by re-signing in
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPw,
      });
      if (signInErr) throw new Error('Current password is incorrect.');

      // Update password
      const { error: updErr } = await supabase.auth.updateUser({ password: newPw });
      if (updErr) throw updErr;

      setSuccess('Password updated.');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      console.error('Password change failed:', err);
      setError(err.message || 'Could not update password.');
    } finally {
      setSaving(false);
    }
  };

  // Simple strength meter
  const strength = useMemo(() => {
    if (!newPw) return 0;
    let s = 0;
    if (newPw.length >= 8)  s++;
    if (newPw.length >= 12) s++;
    if (/[A-Z]/.test(newPw) && /[a-z]/.test(newPw)) s++;
    if (/[0-9]/.test(newPw) && /[^A-Za-z0-9]/.test(newPw)) s++;
    return s;
  }, [newPw]);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : 'repeat(auto-fit, minmax(380px, 1fr))',
      gap: isMobile ? 12 : 16,
    }}>
      <Card style={{ padding: isMobile ? 16 : 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Change password</div>

        {error   && <ErrorBanner message={error} style={{ marginBottom: 12 }} />}
        {success && (
          <div style={{
            padding: '10px 12px', marginBottom: 12,
            background: 'var(--ok-bg)', border: '1px solid rgba(61,199,91,0.22)',
            color: 'var(--ok)', borderRadius: 10, fontSize: 12.5,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Icon.check size={14} />
            {success}
          </div>
        )}

        <FormField
          label="Current password"
          type="password"
          value={currentPw}
          onChange={setCurrentPw}
          icon={Icon.lock}
          autoComplete="current-password"
          disabled={saving}
        />
        <FormField
          label="New password"
          type="password"
          value={newPw}
          onChange={setNewPw}
          icon={Icon.lock}
          autoComplete="new-password"
          disabled={saving}
        />

        {/* Strength meter */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= strength ? 'var(--accent)' : 'var(--border)',
              transition: 'background 0.2s',
            }} />
          ))}
        </div>

        <FormField
          label="Confirm new password"
          type="password"
          value={confirmPw}
          onChange={setConfirmPw}
          icon={Icon.lock}
          autoComplete="new-password"
          disabled={saving}
        />

        <div style={{
          fontSize: 12, color: 'var(--text-3)',
          padding: 10, background: 'var(--surface-2)',
          borderRadius: 8, marginBottom: 12,
        }}>
          At least 8 characters. Mix uppercase, numbers, and symbols for a stronger password.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Btn
            variant="primary"
            size="sm"
            icon={saving ? null : Icon.check}
            onClick={submit}
            disabled={saving || !currentPw || !newPw}
          >
            {saving ? <><Spinner size={12} color="rgba(255,255,255,0.6)" /> Updating…</> : 'Update password'}
          </Btn>
        </div>
      </Card>

      <div>
        {/* 2FA — stub */}
        <Card style={{ padding: 20, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'var(--surface-2)', color: 'var(--text-3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon.shield size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 13.5, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
              }}>
                Two-factor authentication
                <Badge variant="warn">Coming soon</Badge>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                Add an extra layer of security with an authenticator app
              </div>
            </div>
          </div>
        </Card>

        {/* Connected accounts — stub */}
        <Card style={{ padding: 20 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 8,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Connected accounts</div>
            <Badge variant="warn">Coming soon</Badge>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            Sign in with Google, GitHub, or your company SSO. Coming in a future release.
          </div>
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Notifications tab — per-team email/SMS preferences
// ────────────────────────────────────────────────────────────────────────────
function NotificationsTab() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data, error: err } = await supabase
          .from('team_members')
          .select(`
            id, notify_email, sms_opted_in, sms_opt_in_completed, phone_number, language,
            teams ( id, name, sms_enabled, plan )
          `)
          .eq('user_id', user.id);
        if (err) throw err;
        if (!cancelled) setMemberships(data || []);
      } catch (err) {
        console.error('Notifications load failed:', err);
        if (!cancelled) setError(err.message || 'Failed to load preferences.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const updatePref = async (memId, patch) => {
    if (savingId) return;
    setSavingId(memId);
    try {
      const { error: err } = await supabase
        .from('team_members')
        .update(patch)
        .eq('id', memId);
      if (err) throw err;
      setMemberships(prev =>
        prev.map(m => m.id === memId ? { ...m, ...patch } : m)
      );
    } catch (err) {
      console.error('Update preference failed:', err);
      toast.error('Could not save: ' + (err.message || 'unknown error'));
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <Card style={{ padding: 32, textAlign: 'center' }}>
        <Spinner size={16} color="var(--accent)" />
      </Card>
    );
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  if (memberships.length === 0) {
    return (
      <Card style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
          You're not a member of any workspaces yet.
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ padding: isMobile ? 16 : 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
        Notification preferences
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16 }}>
        Choose how each workspace can reach you about document activity.
      </div>

      {memberships.map((m, i) => {
        if (!m.teams) return null;
        const smsAvailable = m.teams.sms_enabled && (m.teams.plan || '').toLowerCase() === 'business';
        // Mirror the backend send gate (/api/send-notification): texts go out
        // only when sms_opted_in && sms_opt_in_completed && phone_number are
        // all set and the team has SMS enabled. Without a completed opt-in
        // (phone + consent captured by the portal welcome modal) there is no
        // number on file, so this toggle can't turn SMS on by itself.
        const smsOptedIn    = m.sms_opted_in === true && m.sms_opt_in_completed === true;
        const smsToggleable = smsAvailable && m.sms_opt_in_completed === true && !!m.phone_number;
        const isSaving = savingId === m.id;
        return (
          <div
            key={m.id}
            style={{
              padding: '14px 0',
              borderTop: i === 0 ? 'none' : '1px solid var(--border)',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 10, gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                {m.teams.name}
              </div>
              {isSaving && (
                <span style={{
                  fontSize: 11, color: 'var(--text-3)',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  <Spinner size={10} color="var(--text-muted)" />
                  Saving…
                </span>
              )}
            </div>

            <PrefRow
              label="Email notifications"
              hint="Document approvals, rejections, and reminders"
              checked={m.notify_email !== false}
              onChange={(v) => updatePref(m.id, { notify_email: v })}
              disabled={isSaving}
            />
            <PrefRow
              label={
                <>
                  SMS notifications
                  {!smsAvailable && (
                    <Badge variant="navy" size="xs" style={{ marginLeft: 6 }}>
                      Business
                    </Badge>
                  )}
                </>
              }
              hint={
                !smsAvailable
                  ? 'Available on Business plan with SMS enabled by your workspace owner'
                  : smsToggleable
                    ? `Time-sensitive alerts via text to ${m.phone_number}`
                    : 'No phone number on file — complete the SMS opt-in prompt in the portal first'
              }
              checked={smsOptedIn}
              onChange={(v) => updatePref(m.id, {
                sms_opted_in: v,
                // Mirror /api/team-members/:id/sms-opt-in: stamp consent time
                // on opt-in, clear it on opt-out.
                sms_opted_in_at: v ? new Date().toISOString() : null,
              })}
              disabled={isSaving || !smsToggleable}
            />
          </div>
        );
      })}
    </Card>
  );
}

function PrefRow({ label, hint, checked, onChange, disabled }) {
  // Whole row is the tap target — the small visual switch is decorative.
  // This is critical on mobile, where the 36×20 switch alone is too small.
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 0',
        width: '100%', textAlign: 'left',
        background: 'transparent', border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit', color: 'inherit',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 1 }}>{hint}</div>
      </div>
      <span
        aria-hidden="true"
        style={{
          width: 36, height: 20, borderRadius: 999,
          background: checked ? 'var(--accent)' : 'var(--border-strong)',
          position: 'relative', display: 'inline-block',
          transition: 'background 0.15s', flexShrink: 0,
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <span style={{
          position: 'absolute', top: 2, left: checked ? 18 : 2,
          width: 16, height: 16, borderRadius: '50%',
          background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          transition: 'left 0.15s',
        }} />
      </span>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Coming soon tab (API + Sessions)
// ────────────────────────────────────────────────────────────────────────────
function ComingSoonTab({ title, desc }) {
  return (
    <Card style={{ padding: 40, textAlign: 'center' }}>
      <div style={{
        width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
        background: 'var(--accent-bg)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon.sparkle size={26} stroke="var(--accent-600)" />
      </div>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '3px 10px', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        background: 'var(--accent)', color: '#fff', borderRadius: 999,
        marginBottom: 14,
      }}>
        Coming soon
      </div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{title}</h2>
      <p style={{
        fontSize: 13.5, color: 'var(--text-3)',
        maxWidth: 420, margin: '0 auto', lineHeight: 1.5,
      }}>
        {desc}
      </p>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helper field
// ────────────────────────────────────────────────────────────────────────────
function FormField({ label, value, onChange, icon, type, readOnly, autoComplete, disabled, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{
        fontSize: 12, fontWeight: 600, color: 'var(--text-2)',
        display: 'block', marginBottom: 6,
      }}>
        {label}
      </label>
      <Input
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        icon={icon}
        type={type}
        readOnly={readOnly}
        autoComplete={autoComplete}
        disabled={disabled}
        style={readOnly ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
      />
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Billing tab — account-level plan management
// Plans are user-level (Phase 2), so this lives naturally on Account.
// ────────────────────────────────────────────────────────────────────────────
const BILLING_PLANS = [
  {
    key: 'free',
    name: 'Free',
    monthly: 0, annual: 0,
    tagline: 'To try it out',
    metrics: {
      ai: '30 / month',
      storage: '25 documents',
      workspaces: '1 workspace',
      members: '10 staff members',
    },
    features: [
      'Email notifications',
      'Full review workflow',
      'Unlimited client portal users',
    ],
    notIncluded: [
      'Custom branding',
      'SMS notifications',
      'Priority support',
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    monthly: 29.99, annual: 290,
    tagline: 'For growing practices',
    metrics: {
      ai: 'Unlimited',
      storage: '500 documents',
      workspaces: 'Up to 3 workspaces',
      members: 'Unlimited staff',
    },
    features: [
      'Everything in Free',
      'Custom branding (logo + colors)',
      'Expiration tracking + reminders',
      'Priority email support',
    ],
    notIncluded: [
      'SMS notifications',
    ],
    popular: true,
  },
  {
    key: 'business',
    name: 'Business',
    monthly: 49, annual: 470,
    tagline: 'For agencies and multi-office',
    metrics: {
      ai: 'Unlimited',
      storage: 'Unlimited',
      workspaces: 'Unlimited',
      members: 'Unlimited',
    },
    features: [
      'Everything in Pro',
      'SMS notifications (Twilio)',
      'Priority support',
      'Audit log export',
    ],
    notIncluded: [],
  },
];

function BillingTab() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [userPlanInfo, setUserPlanInfo]       = useState(null);
  const [poolUsage, setPoolUsage]             = useState(null);
  const [billingCycle, setBillingCycle]       = useState('monthly');
  const [loadingCheckout, setLoadingCheckout] = useState(null);
  const [loadingPortal, setLoadingPortal]     = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const plan = await getUserPlan(user.id);
        const usage = await getOwnerUsage(user.id);
        if (cancelled) return;
        setUserPlanInfo(plan);
        setPoolUsage(usage);
      } catch (err) {
        console.warn('Billing tab load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const startCheckout = async (planKey, billing) => {
    const loadingKey = `${planKey}_${billing}`;
    if (loadingCheckout) return;
    setError(null);
    setLoadingCheckout(loadingKey);
    try {
      const res = await api('/api/create-checkout', { plan: planKey, billing });
      if (res.url) {
        window.location.href = res.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      setError(err.message || 'Could not start checkout.');
      setLoadingCheckout(null);
    }
  };

  const openPortal = async () => {
    if (loadingPortal) return;
    setError(null);
    setLoadingPortal(true);
    try {
      const res = await api('/api/billing-portal', {});
      if (res.url) {
        window.location.href = res.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err) {
      console.error('Portal failed:', err);
      setError(err.message || 'Could not open billing portal.');
      setLoadingPortal(false);
    }
  };

  if (loading) {
    return (
      <Card style={{ padding: 48, textAlign: 'center' }}>
        <Spinner size={20} color="var(--accent)" />
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-3)' }}>Loading billing info…</div>
      </Card>
    );
  }

  const currentPlanKey = userPlanInfo?.plan || 'free';
  const currentPlan = BILLING_PLANS.find(p => p.key === currentPlanKey) || BILLING_PLANS[0];
  const aiUsed = poolUsage?.totalUsed ?? 0;
  const aiLimit = userPlanInfo?.limits?.ai ?? 30;
  const ownedWorkspaces = poolUsage?.teams?.length ?? 0;
  const isUnlimited = currentPlanKey === 'business';

  return (
    <div>
      {error && <ErrorBanner message={error} style={{ marginBottom: 16 }} />}

      {/* Current plan hero */}
      <Card style={{
        padding: isMobile ? 18 : 24,
        marginBottom: 24,
        background: 'linear-gradient(135deg, #0a0f2e 0%, #1e2654 100%)',
        color: '#fff', border: 'none',
      }}>
        <Badge
          size="xs"
          style={{
            background: 'rgba(139,232,158,0.15)', color: '#8be89e',
            border: '1px solid rgba(61,199,91,0.28)',
          }}
        >
          Current plan
        </Badge>
        <div style={{
          fontSize: isMobile ? 26 : 32,
          fontWeight: 700, letterSpacing: '-0.025em',
          marginTop: 12, marginBottom: 4,
        }}>
          {currentPlan.name}
          {currentPlan.key !== 'free' && (
            <span style={{
              fontSize: isMobile ? 15 : 18,
              fontWeight: 500, color: 'rgba(255,255,255,0.7)', marginLeft: 8,
            }}>
              · ${currentPlan.monthly}/mo
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 20 }}>
          {currentPlan.metrics.ai} AI · {currentPlan.metrics.storage} · {currentPlan.metrics.workspaces}
        </div>

        {/* Usage for non-Business plans */}
        {!isUnlimited && aiLimit > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: 12, marginBottom: 6,
              color: 'rgba(255,255,255,0.85)',
            }}>
              <span>AI classifications used</span>
              <strong>{aiUsed} / {aiLimit}</strong>
            </div>
            <div style={{
              height: 6, borderRadius: 3,
              background: 'rgba(255,255,255,0.15)',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${Math.min(100, Math.round((aiUsed / aiLimit) * 100))}%`,
                background: '#8be89e',
                transition: 'width 0.4s ease',
              }} />
            </div>
            {ownedWorkspaces > 0 && (
              <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>
                Shared across {ownedWorkspaces} workspace{ownedWorkspaces === 1 ? '' : 's'} you own
              </div>
            )}
          </div>
        )}

        {/* CTAs */}
        <div style={{
          display: 'flex', gap: 8,
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          {currentPlan.key === 'free' ? (
            <Btn
              variant="primary" size="md"
              iconRight={Icon.arrowR}
              onClick={() => document.getElementById('billing-plans')?.scrollIntoView({ behavior: 'smooth' })}
              style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
            >
              See upgrade options
            </Btn>
          ) : (
            <>
              <Btn
                variant="outline" size="md"
                icon={loadingPortal ? null : Icon.card}
                onClick={openPortal}
                disabled={loadingPortal}
                style={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.3)',
                  background: 'rgba(255,255,255,0.08)',
                  ...(isMobile ? { width: '100%', justifyContent: 'center' } : {}),
                }}
              >
                {loadingPortal ? <><Spinner size={14} /> Opening…</> : 'Manage billing'}
              </Btn>
              {currentPlan.key === 'pro' && (
                <Btn
                  variant="primary" size="md"
                  iconRight={Icon.arrowR}
                  onClick={() => document.getElementById('billing-plans')?.scrollIntoView({ behavior: 'smooth' })}
                  style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
                >
                  Upgrade to Business
                </Btn>
              )}
              {currentPlan.key === 'business' && (
                <span style={{
                  fontSize: 13, color: '#8be89e', fontWeight: 600,
                  alignSelf: 'center',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                  <Icon.check size={14} strokeWidth={2.5} />
                  You're on our top plan
                </span>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Plans grid */}
      <div id="billing-plans" style={{
        marginTop: 32, marginBottom: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>Plans & pricing</h2>
          <p style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>
            Your plan applies to all workspaces you own.
          </p>
        </div>
        <div style={{
          display: 'flex', background: 'var(--surface-2)',
          border: '1px solid var(--border)', borderRadius: 8, padding: 2,
        }}>
          {['monthly', 'annual'].map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setBillingCycle(c)}
              style={{
                padding: '6px 14px', fontSize: 12, fontWeight: 600,
                border: 'none', borderRadius: 6, cursor: 'pointer',
                background: billingCycle === c ? 'var(--surface)' : 'transparent',
                color: billingCycle === c ? 'var(--text)' : 'var(--text-3)',
                fontFamily: 'inherit',
              }}
            >
              {c === 'monthly' ? 'Monthly' : 'Annual'}
              {c === 'annual' && (
                <span style={{ marginLeft: 6, color: 'var(--accent-600)' }}>–19%</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? '1fr'
          : 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 14,
      }}>
        {BILLING_PLANS.map(plan => (
          <BillingPlanCard
            key={plan.key}
            plan={plan}
            cycle={billingCycle}
            isCurrent={plan.key === currentPlanKey}
            onUpgrade={() => startCheckout(plan.key, billingCycle)}
            loadingKey={loadingCheckout}
          />
        ))}
      </div>
    </div>
  );
}

function BillingPlanCard({ plan, cycle, isCurrent, onUpgrade, loadingKey }) {
  const price = cycle === 'annual' ? plan.annual : plan.monthly;
  const myKey = `${plan.key}_${cycle}`;
  const isLoading = loadingKey === myKey;
  const monthlyEq = cycle === 'annual' && plan.annual > 0 ? `$${(plan.annual / 12).toFixed(0)}/mo equivalent` : null;

  return (
    <Card style={{
      padding: 20, position: 'relative',
      display: 'flex', flexDirection: 'column',
      border: plan.popular ? '2px solid var(--accent)' : '1px solid var(--border)',
    }}>
      {plan.popular && (
        <div style={{
          position: 'absolute', top: -10, right: 16,
          background: 'var(--accent)', color: '#fff',
          padding: '2px 10px', borderRadius: 999,
          fontSize: 10, fontWeight: 800, letterSpacing: '0.05em',
        }}>
          MOST POPULAR
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
          {plan.name}
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text)' }}>
          {price === 0 ? 'Free' : `$${price}`}
          {price > 0 && (
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)' }}>
              {' '}/ {cycle === 'annual' ? 'year' : 'month'}
            </span>
          )}
        </div>
        {monthlyEq && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{monthlyEq}</div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
          {plan.tagline}
        </div>
      </div>

      <div style={{
        padding: '10px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
        marginBottom: 14, fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6,
      }}>
        <div><strong style={{ color: 'var(--text)' }}>{plan.metrics.ai}</strong> AI classifications</div>
        <div>{plan.metrics.storage}</div>
        <div>{plan.metrics.workspaces}</div>
        <div>{plan.metrics.members}</div>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 16, flex: 1 }}>
        {plan.features.map((f, i) => (
          <li key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '4px 0', fontSize: 12.5, color: 'var(--text-2)',
          }}>
            <Icon.check size={13} stroke="var(--accent-600)" style={{ marginTop: 2, flexShrink: 0 }} />
            {f}
          </li>
        ))}
        {plan.notIncluded.map((f, i) => (
          <li key={`x-${i}`} style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '4px 0', fontSize: 12.5, color: 'var(--text-muted)',
            textDecoration: 'line-through',
          }}>
            <Icon.x size={13} stroke="var(--text-muted)" style={{ marginTop: 2, flexShrink: 0 }} />
            {f}
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <Btn variant="outline" size="md" disabled style={{ width: '100%' }}>
          Current plan
        </Btn>
      ) : plan.key === 'free' ? (
        <Btn variant="outline" size="md" disabled style={{ width: '100%' }}>
          {/* Free is default — no downgrade button here, use the portal */}
          Contact support to downgrade
        </Btn>
      ) : (
        <Btn
          variant="primary" size="md"
          iconRight={isLoading ? null : Icon.arrowR}
          onClick={onUpgrade}
          disabled={isLoading || !!loadingKey}
          style={{ width: '100%' }}
        >
          {isLoading ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Loading…</> : `Upgrade to ${plan.name}`}
        </Btn>
      )}
    </Card>
  );
}
